import { FC, useMemo, useState } from 'react';
import { CustomModal } from 'components/uikit/modal/CustomModal.tsx';
import { Button, ButtonColor } from 'components/uikit/buttons/Button.tsx';
import styles from './StartSigningModal.module.scss';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import {
  MultipleSelector,
  MultipleSelectorOption,
} from 'components/uikit/selector/MultipleSelector.tsx';
import { QueryFactory } from 'services/api/index.ts';
import { Status } from 'services/api/api-client.types.ts';
import { useDocumentSigningStart } from './useDocumentSigningStart.tsx';
import { Loading } from 'components/uikit/suspense/Loading.tsx';

export type StartSigningModalProps = {
  projectId: number;
  revisionId: number;
  isOpen: boolean;
  onClose: () => void;
};

export const StartSigningModal: FC<StartSigningModalProps> = (props) => {
  const i18n = useScopedTranslation('Component.StartSigningModal');
  const buttonTitle = useScopedTranslation('Button.Title');
  const [selectedOptions, setSelectedOptions] = useState<
    MultipleSelectorOption[]
  >([]);
  const documentsQuery = QueryFactory.DocumentQuery.useSearchQuery(
    {
      revisionId: props.revisionId,
      status: Status.CanBeSigned,
      hasRoles: true,
    },
    { enabled: !!props.revisionId && props.isOpen },
  );

  const options = useMemo<MultipleSelectorOption[]>(() => {
    return (
      documentsQuery.data?.data.map((document) => {
        return { id: document.id, label: document.name };
      }) ?? []
    );
  }, [documentsQuery.data]);

  const { isStarting, start } = useDocumentSigningStart({
    revisionId: props.revisionId,
    projectId: props.projectId,
    documentIds: selectedOptions.map((option) => option.id as number),
    onFinished: props.onClose,
  });

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      isBlocking={true}
      title={i18n.t('title')}
    >
      <Loading loading={isStarting || documentsQuery.isLoading}>
        <div className={styles.message}>
          <p>{i18n.t('message')}</p>

          <div className={styles.selector}>
            <MultipleSelector
              showSelectAll
              selectAllOnMount
              onSelectionChange={setSelectedOptions}
              options={options}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            title={buttonTitle.t('cancel')}
            color={ButtonColor.Secondary}
            className={styles.button}
            onClick={props.onClose}
          />
          <Button
            testId={'start-button'}
            title={buttonTitle.t('sign')}
            color={ButtonColor.Primary}
            className={styles.button}
            disabled={selectedOptions.length === 0}
            onClick={start}
          />
        </div>
      </Loading>
    </CustomModal>
  );
};
